// Legal content for the Privacy Policy and Terms of Service pages.
// NOTE: These are good-faith starting templates and are NOT legal advice.
// Have them reviewed by a qualified attorney and fill in the bracketed
// placeholders (legal entity name, governing jurisdiction) before relying on them.

const CONTACT_EMAIL = 'collab@thirdrailavenue.com';
const UPDATED = 'June 8, 2026';

export const privacy = {
  slug: 'privacy',
  title: 'Privacy Policy',
  updated: UPDATED,
  intro:
    'This Privacy Policy explains how ThirdRail Avenue (“we”, “us”, or “our”) collects, uses, and protects information when you visit our website or contact us. By using this site, you agree to the practices described below.',
  sections: [
    {
      heading: 'Information we collect',
      body: ['We collect only what we need to respond to you and run our website:'],
      list: [
        'Information you provide: when you submit our contact form, we collect your name, email address, the inquiry type you select, and the contents of your message.',
        'Technical information: like most websites, our hosting and infrastructure providers may automatically log standard data such as your IP address, browser type, and pages visited, for security and performance.',
        'We do not collect payment information through this website.',
      ],
    },
    {
      heading: 'How we use your information',
      body: ['We use the information we collect to:'],
      list: [
        'Respond to your inquiries and communicate with you about a potential engagement.',
        'Provide, maintain, and improve our website and services.',
        'Protect against fraud, abuse, and security issues.',
      ],
    },
    {
      heading: 'How information is shared',
      body: [
        'We do not sell your personal information. We share information only with service providers that help us operate the site and respond to you — for example our form-processing provider, website host, and email provider — and only as needed to perform those services.',
        'We may also disclose information if required by law or to protect our rights, users, or the public.',
      ],
    },
    {
      heading: 'Cookies and analytics',
      body: [
        'This site uses minimal cookies necessary for it to function. We may use privacy-respecting analytics to understand how visitors use the site in aggregate. You can control cookies through your browser settings.',
      ],
    },
    {
      heading: 'Data retention',
      body: [
        'We keep contact submissions for as long as needed to respond to your inquiry and for our legitimate business records, after which we delete or anonymize them.',
      ],
    },
    {
      heading: 'Your rights',
      body: [
        `Depending on where you live, you may have the right to access, correct, or delete the personal information we hold about you, or to object to certain processing. To make a request, email us at ${CONTACT_EMAIL} and we will respond as required by applicable law.`,
      ],
    },
    {
      heading: 'Data security',
      body: [
        'We take reasonable measures to protect your information. However, no method of transmission or storage over the internet is completely secure, and we cannot guarantee absolute security.',
      ],
    },
    {
      heading: "Children's privacy",
      body: [
        'This website is not directed to children under 16, and we do not knowingly collect personal information from them. If you believe a child has provided us information, please contact us and we will delete it.',
      ],
    },
    {
      heading: 'Changes to this policy',
      body: [
        'We may update this Privacy Policy from time to time. Material changes will be reflected by updating the date at the top of this page.',
      ],
    },
    {
      heading: 'Contact us',
      body: [`Questions about this policy? Email us at ${CONTACT_EMAIL}.`],
    },
  ],
};

export const terms = {
  slug: 'terms',
  title: 'Terms of Service',
  updated: UPDATED,
  intro:
    'These Terms of Service (“Terms”) govern your use of the ThirdRail Avenue website. By accessing or using this site, you agree to these Terms. If you do not agree, please do not use the site.',
  sections: [
    {
      heading: 'Use of the site',
      body: [
        'You may use this website for lawful, informational purposes. You agree not to misuse the site, attempt to disrupt it, access it through automated means without permission, or use it in any way that infringes the rights of others.',
      ],
    },
    {
      heading: 'Intellectual property',
      body: [
        'The content on this site — including text, graphics, logos, and design — is owned by or licensed to ThirdRail Avenue and is protected by intellectual property laws. You may not reproduce or distribute it without our permission. Third-party names and logos shown on the site are the property of their respective owners.',
      ],
    },
    {
      heading: 'Services and engagements',
      body: [
        'This website is informational. Any working relationship between ThirdRail Avenue and a brand or creator is governed by a separate written agreement, not by this site. Case studies and figures shown for illustrative purposes are examples and are not guarantees of any particular result.',
      ],
    },
    {
      heading: 'Third-party links',
      body: [
        'Our site may link to third-party websites and platforms (such as creator channels). We are not responsible for the content, policies, or practices of those third parties.',
      ],
    },
    {
      heading: 'Disclaimers',
      body: [
        "The site is provided on an 'as is' and 'as available' basis without warranties of any kind, whether express or implied, including fitness for a particular purpose and non-infringement, to the fullest extent permitted by law.",
      ],
    },
    {
      heading: 'Limitation of liability',
      body: [
        'To the fullest extent permitted by law, ThirdRail Avenue will not be liable for any indirect, incidental, or consequential damages arising from your use of, or inability to use, this website.',
      ],
    },
    {
      heading: 'Changes to these terms',
      body: [
        'We may update these Terms from time to time. Continued use of the site after changes take effect constitutes acceptance of the updated Terms.',
      ],
    },
    {
      heading: 'Governing law',
      body: [
        'These Terms are governed by the laws of [your governing jurisdiction], without regard to its conflict-of-laws principles.',
      ],
    },
    {
      heading: 'Contact us',
      body: [`Questions about these Terms? Email us at ${CONTACT_EMAIL}.`],
    },
  ],
};
